import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, ArrowRight, Building2, Pencil, Plus, RefreshCw, StickyNote, Trash2, Webhook, X } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { useSetActiveCompany } from "./use-set-active-company";
import { BillingSection } from "./billing";
import { EntsoeTokenSection } from "./entsoe";
import { CreateCompanyUserForm, UserTable } from "./user-management";
import type { AdminUser } from "./shared";
import {
  AdminPageHeader,
  Button,
  Callout,
  FormField,
  Input,
  KeyValue,
  Metric,
  Panel,
  Select,
  StateChip,
  Textarea,
  errorMessage,
  formatDate,
  formatMoney,
  humanize,
  type ClientListItem,
} from "./client-operations-shared";

type CreateClientResponse = {
  client: { id: string; name: string; slug: string };
  operation: { id: string; state: string };
};

type PaddlePartyResponse = {
  party: {
    customer: { id: string; name: string | null; email: string; status: string };
    businesses: Array<{
      id: string;
      name: string;
      companyNumber: string | null;
      taxIdentifier: string | null;
      contacts: Array<{ name: string | null; email: string }>;
      status: string;
    }>;
    addresses: Array<{
      id: string;
      description: string | null;
      firstLine: string | null;
      secondLine: string | null;
      city: string | null;
      postalCode: string | null;
      region: string | null;
      countryCode: string;
      status: string;
    }>;
  };
  reconciliationStatus: string;
  lastReconciledAt: string | null;
  readAt: string;
  source: "paddle_live";
  changedFields?: string[];
};

type PaddlePartyDraft = {
  partyKind: "business" | "individual";
  customerName: string;
  customerEmail: string;
  businessName: string;
  companyNumber: string;
  taxIdentifier: string;
  contacts: Array<{ name: string; email: string }>;
  description: string;
  firstLine: string;
  secondLine: string;
  city: string;
  postalCode: string;
  region: string;
  countryCode: string;
};

type ClientTimelineResponse = {
  timeline: Array<{
    id: string;
    source: "cascena" | "paddle";
    at: string;
    title: string;
    detail: string | null;
    severity: string;
    category: string;
    actor: string;
    route: string | null;
    method: string | null;
    statusCode: number | null;
    providerEventId: string | null;
    processingState: string | null;
    receivedAt?: string;
  }>;
  limit: number;
  sources: Array<"cascena" | "paddle">;
};

type ClientAggregate = {
  company: {
    id: string;
    name: string;
    slug: string;
    eexPlatformName: string | null;
    eexEnabled: boolean;
    tradingEnabled: boolean;
    forecastingEnabled: boolean;
    brmEnabled: boolean;
    disabledAt: string | null;
    createdAt: string;
  };
  profile: {
    segment: string | null;
    primaryContactName: string | null;
    primaryContactEmail: string | null;
    primaryContactPhone: string | null;
    contractReference: string | null;
    committedTermStart: string | null;
    committedTermEnd: string | null;
    negotiatedTermsNote: string | null;
    moduleIntent: Record<string, boolean>;
    lifecycle: "onboarding" | "live" | "offboarding";
    orderFormRef: string | null;
    poRequired: boolean;
  } | null;
  onboarding: {
    status: string;
    invoiceNumber: string | null;
    amountMinor: number | null;
    currency: string | null;
    billedAt: string | null;
    dueAt: string | null;
    paidAt: string | null;
  } | null;
  billingAccount: {
    paddleCustomerId: string | null;
    paddleBusinessId: string | null;
    paddleAddressId: string | null;
    paddleEnvironment: string;
    reconciliationStatus: string;
    lastReconciledAt: string | null;
  } | null;
  operation: {
    id: string;
    state: string;
    workflowVersion: number;
    collectionVariant: string;
    checkoutEnabled: boolean;
    transactionId: string | null;
    subscriptionId: string | null;
    lastErrorCode: string | null;
    updatedAt: string;
  } | null;
  subscription: {
    status: string;
    tier: string;
    currency: string;
    collectionMode: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    nextBilledAt: string | null;
    scheduledCancelAt: string | null;
    scheduledPauseAt: string | null;
    graceUntil: string | null;
    paddleSubscriptionId: string | null;
  } | null;
  subscriptionView: "current" | "latest_canceled" | "none";
  notes: Array<{
    id: string;
    body: string;
    pinned: boolean;
    authorUserId: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  userCounts: { total: number; active: number };
  recentInvoices: Array<{
    paddleTransactionId: string;
    invoiceNumber: string | null;
    status: string;
    currency: string;
    totalMinor: number;
    paymentTermsDays: number | null;
    collectionMode: string | null;
    dueAt: string | null;
    billedAt: string | null;
    paidAt: string | null;
  }>;
};

const MODULES = [
  ["eex", "EEX"],
  ["trading", "Trading"],
  ["forecasting", "Forecasting"],
  ["brm", "BRM"],
] as const;

export const CLIENT_DETAIL_TABS = [
  "overview",
  "onboarding",
  "profile",
  "billing",
  "invoices",
  "subscription",
  "timeline",
  "users",
  "data",
  "notes",
] as const;
export type ClientDetailTab = (typeof CLIENT_DETAIL_TABS)[number];

export function isClientDetailTab(value: unknown): value is ClientDetailTab {
  return typeof value === "string" && CLIENT_DETAIL_TABS.includes(value as ClientDetailTab);
}

export function ClientsWorkspace() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const clientsQ = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => api<{ clients: ClientListItem[] }>("/admin/clients"),
  });
  const clients = clientsQ.data?.clients ?? [];
  const summary = useMemo(() => ({
    onboarding: clients.filter((client) => client.lifecycle === "onboarding").length,
    live: clients.filter((client) => client.lifecycle === "live" && !client.disabledAt).length,
    attention: clients.filter((client) => client.unpaidInvoiceCount > 0 || client.operationState === "reconciliation_required" || client.operationState === "issue_uncertain").length,
  }), [clients]);

  return (
    <div className="space-y-5">
      <AdminPageHeader
        eyebrow="Client operations"
        title="Clients"
        description="One operational record per customer—from signed terms and Paddle invoicing through access and ongoing service."
        actions={<Button variant="primary" onClick={() => setCreating(true)}><Plus size={14} /> New client</Button>}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Metric label="In onboarding" value={summary.onboarding} detail="Commercial or setup work remains" />
        <Metric label="Live clients" value={summary.live} tone="good" detail="Active service lifecycle" />
        <Metric label="Needs attention" value={summary.attention} tone={summary.attention ? "warning" : "neutral"} detail="Invoice or reconciliation blocker" />
      </div>

      {creating && (
        <CreateClientPanel
          onClose={() => setCreating(false)}
          onCreated={async (result) => {
            setCreating(false);
            await queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
            await navigate({ to: "/admin/onboarding/$operationId", params: { operationId: result.operation.id } });
          }}
        />
      )}

      <Panel>
        {clientsQ.isLoading ? (
          <div className="p-8 text-center text-sm text-muted">Loading clients…</div>
        ) : clientsQ.isError ? (
          <div className="p-4"><Callout tone="danger" title="Clients could not be loaded">{errorMessage(clientsQ.error)}</Callout></div>
        ) : clients.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 size={24} className="mx-auto text-muted" />
            <div className="mt-2 text-sm font-medium">No clients match this view</div>
            <p className="mt-1 text-xs text-muted">Create the first controlled onboarding record.</p>
          </div>
        ) : (
          <div className="ops-admin-table-scroll overflow-x-auto">
            <table className="w-full min-w-[940px] text-left text-xs">
              <thead className="border-b border-border/70 bg-border/10 text-[11px] uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Client</th>
                  <th className="px-3 py-2.5 font-medium">Lifecycle</th>
                  <th className="px-3 py-2.5 font-medium">Onboarding</th>
                  <th className="px-3 py-2.5 font-medium">Subscription</th>
                  <th className="px-3 py-2.5 font-medium">People</th>
                  <th className="px-3 py-2.5 font-medium">Attention</th>
                  <th className="px-3 py-2.5 text-right font-medium">Next</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} className="border-b border-border/50 last:border-0 hover:bg-border/10">
                    <td className="px-3 py-3">
                      <Link to="/admin/clients/$clientId" params={{ clientId: client.id }} className="font-medium text-fg hover:text-accent">{client.name}</Link>
                      <div className="mt-0.5 text-[11px] text-muted">{client.segment ?? client.slug}</div>
                    </td>
                    <td className="px-3 py-3"><StateChip value={client.disabledAt ? "disabled" : client.lifecycle} /></td>
                    <td className="px-3 py-3">
                      <StateChip value={client.onboardingStatus} />
                      {client.operationState && <div className="mt-1 text-[11px] text-muted">Workflow: {humanize(client.operationState)}</div>}
                      {client.billingPartyStatus && <div className="mt-1 text-[11px] text-muted">Party: {humanize(client.billingPartyStatus)}</div>}
                    </td>
                    <td className="px-3 py-3">
                      <StateChip
                        value={client.subscriptionDisplayStatus}
                        label={client.subscriptionDisplayStatus === "none_legacy_access" ? "None — legacy access" : undefined}
                      />
                      {client.nextBilledAt && <div className="mt-1 text-[11px] text-muted">Next {formatDate(client.nextBilledAt)}</div>}
                    </td>
                    <td className="px-3 py-3 tabular-nums">{client.userCount}</td>
                    <td className="px-3 py-3">
                      {client.unpaidInvoiceCount > 0 ? <StateChip value="past_due" label={`${client.unpaidInvoiceCount} unpaid`} /> : <span className="text-muted">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {client.operationId && !client.operationState?.includes("complete") ? (
                        <Link to="/admin/onboarding/$operationId" params={{ operationId: client.operationId }} className="inline-flex items-center gap-1 font-medium text-accent hover:underline">Resume <ArrowRight size={13} /></Link>
                      ) : (
                        <Link to="/admin/clients/$clientId" params={{ clientId: client.id }} className="inline-flex items-center gap-1 text-muted hover:text-fg">Open <ArrowRight size={13} /></Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function CreateClientPanel({ onClose, onCreated }: { onClose(): void; onCreated(result: CreateClientResponse): void }) {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    segment: "",
    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    note: "",
    moduleIntent: { eex: true, trading: false, forecasting: false, brm: false } as Record<string, boolean>,
  });
  const mutation = useMutation({
    mutationFn: () => api<CreateClientResponse>("/admin/clients", {
      method: "POST",
      body: JSON.stringify({
        name: form.name,
        ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
        segment: form.segment || null,
        primaryContactName: form.primaryContactName || null,
        primaryContactEmail: form.primaryContactEmail || null,
        primaryContactPhone: form.primaryContactPhone || null,
        moduleIntent: form.moduleIntent,
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      }),
    }),
    onSuccess: onCreated,
  });
  const duplicate = mutation.error instanceof ApiError && mutation.error.status === 409
    ? mutation.error.body as { companyId?: string; operationId?: string } | undefined
    : null;

  return (
    <Panel title="Create client record" description="Creates the local onboarding workspace only. It does not invoice, subscribe, or grant access." action={<Button variant="quiet" onClick={onClose} aria-label="Close"><X size={15} /></Button>}>
      <form className="space-y-4 p-4" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
        <Callout title="Safe starting point">No Paddle write happens here. The mandatory onboarding category and recurring package are added later from the server-owned catalog.</Callout>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <FormField label="Company name" required><Input required minLength={2} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></FormField>
          <FormField label="Slug" hint="optional"><Input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value.toLowerCase() })} placeholder="generated-from-name" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" /></FormField>
          <FormField label="Segment" hint="optional"><Input value={form.segment} onChange={(event) => setForm({ ...form, segment: event.target.value })} placeholder="Enterprise" /></FormField>
          <FormField label="Primary contact" hint="operational"><Input value={form.primaryContactName} onChange={(event) => setForm({ ...form, primaryContactName: event.target.value })} /></FormField>
          <FormField label="Contact email" hint="not invoice identity"><Input type="email" value={form.primaryContactEmail} onChange={(event) => setForm({ ...form, primaryContactEmail: event.target.value })} /></FormField>
          <FormField label="Contact phone" hint="optional"><Input value={form.primaryContactPhone} onChange={(event) => setForm({ ...form, primaryContactPhone: event.target.value })} /></FormField>
        </div>
        <div>
          <div className="text-xs font-medium text-fg">Module intent</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {MODULES.map(([key, label]) => (
              <label key={key} className="inline-flex items-center gap-2 rounded-md border border-border px-2.5 py-2 text-xs">
                <input type="checkbox" checked={Boolean(form.moduleIntent[key])} onChange={(event) => setForm({ ...form, moduleIntent: { ...form.moduleIntent, [key]: event.target.checked } })} />
                {label}
              </label>
            ))}
          </div>
        </div>
        <FormField label="Owner note" hint="optional; stored in Cascena"><Textarea value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="Commercial context or handover note" /></FormField>
        {mutation.isError && (
          <Callout tone="danger" title={duplicate ? "A matching client already exists" : "Client was not created"}>
            {duplicate?.operationId ? "Open the existing onboarding operation instead of creating a duplicate." : errorMessage(mutation.error)}
          </Callout>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || form.name.trim().length < 2}>{mutation.isPending ? "Creating…" : "Create and start onboarding"}</Button>
        </div>
      </form>
    </Panel>
  );
}

export function ClientDetailWorkspace({ clientId, tab, onTabChange }: {
  clientId: string;
  tab: ClientDetailTab;
  onTabChange(tab: ClientDetailTab): void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setActiveCompany = useSetActiveCompany();
  const detailQ = useQuery({
    queryKey: ["admin", "client", clientId],
    queryFn: () => api<ClientAggregate>(`/admin/clients/${clientId}`),
  });
  const startOnboarding = useMutation({
    mutationFn: () => api<{ operation: { id: string } }>("/admin/onboarding", { method: "POST", body: JSON.stringify({ companyId: clientId }) }),
    onSuccess: (result) => navigate({ to: "/admin/onboarding/$operationId", params: { operationId: result.operation.id } }),
  });
  if (detailQ.isLoading) return <div className="py-12 text-center text-sm text-muted">Loading client…</div>;
  if (detailQ.isError || !detailQ.data) return <Callout tone="danger" title="Client could not be loaded">{errorMessage(detailQ.error)}</Callout>;
  const data = detailQ.data;
  const operationId = data.operation?.id;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        eyebrow="Client 360"
        title={data.company.name}
        description={`${data.company.slug} · Created ${formatDate(data.company.createdAt)} · Cascena and Paddle states remain deliberately separate.`}
        actions={
          <>
            <Button onClick={() => onTabChange("billing")}>Billing</Button>
            <Button onClick={() => { setActiveCompany(clientId); void navigate({ to: "/" }); }}>Open client app</Button>
            {operationId ? (
              <Button variant="primary" onClick={() => navigate({ to: "/admin/onboarding/$operationId", params: { operationId } })}>{data.operation?.state === "complete" ? "View onboarding" : "Resume onboarding"} <ArrowRight size={14} /></Button>
            ) : (
              <Button variant="primary" disabled={startOnboarding.isPending} onClick={() => startOnboarding.mutate()}>Start onboarding</Button>
            )}
          </>
        }
      />
      {data.company.disabledAt && <Callout tone="danger" title="Client is disabled">Access is disabled independently of Paddle billing state.</Callout>}
      <nav aria-label="Client sections" className="ops-admin-client-tabs flex gap-1 overflow-x-auto border-b border-border">
        {CLIENT_DETAIL_TABS.map((value) => (
          <button key={value} onClick={() => onTabChange(value)} className={`border-b-2 px-3 py-2 text-xs font-medium ${tab === value ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"}`}>{humanize(value)}</button>
        ))}
      </nav>
      {tab === "overview" && <ClientOverview data={data} />}
      {tab === "onboarding" && <ClientOnboarding data={data} onOpen={() => operationId && navigate({ to: "/admin/onboarding/$operationId", params: { operationId } })} />}
      {tab === "profile" && <ClientProfile data={data} onSaved={() => queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] })} />}
      {tab === "billing" && <BillingSection companyId={clientId} />}
      {tab === "invoices" && <ClientInvoices invoices={data.recentInvoices} />}
      {tab === "subscription" && <ClientSubscription clientId={clientId} subscription={data.subscription} subscriptionView={data.subscriptionView} paymentTermsDays={data.recentInvoices.find((invoice) => invoice.collectionMode === "manual")?.paymentTermsDays ?? null} />}
      {tab === "timeline" && <ClientTimeline clientId={clientId} companyName={data.company.name} />}
      {tab === "users" && <ClientUsers clientId={clientId} />}
      {tab === "data" && <EntsoeTokenSection isGlobalAdmin activeCompanyId={clientId} companyIdOverride={clientId} />}
      {tab === "notes" && <ClientNotes clientId={clientId} notes={data.notes} />}
    </div>
  );
}

function ClientOverview({ data }: { data: ClientAggregate }) {
  const pinnedNote = data.notes.find((note) => note.pinned) ?? null;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Lifecycle" value={<StateChip value={data.profile?.lifecycle} />} detail={data.profile?.segment ?? "No segment"} />
        <Metric label="Onboarding purchase" value={<StateChip value={data.onboarding?.status} />} detail={data.onboarding?.invoiceNumber ?? "No invoice yet"} />
        <Metric label="Subscription" value={<StateChip value={data.subscription?.status} />} detail={data.subscriptionView === "latest_canceled" ? "Latest canceled subscription" : data.subscription?.tier ? humanize(data.subscription.tier) : data.company.eexEnabled ? "None — legacy access" : "No subscription"} />
        <Metric label="Active users" value={data.userCounts.active} detail={`${data.userCounts.total} total records`} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Commercial file" description="Cascena-owned agreement context—not the legal invoice identity.">
          <dl className="px-4 py-1">
            <KeyValue label="Contract" value={data.profile?.contractReference} />
            <KeyValue label="Order form" value={data.profile?.orderFormRef} />
            <KeyValue label="Committed term" value={data.profile?.committedTermStart && data.profile.committedTermEnd ? `${formatDate(data.profile.committedTermStart)} – ${formatDate(data.profile.committedTermEnd)}` : "—"} />
            <KeyValue label="PO required" value={data.profile?.poRequired ? "Yes" : "No"} />
          </dl>
        </Panel>
        <Panel title="Paddle billing identity" description="Paddle remains authoritative; Cascena stores only linked IDs.">
          <dl className="px-4 py-1">
            <KeyValue label="Environment" value={<StateChip value={data.billingAccount?.paddleEnvironment} />} />
            <KeyValue label="Party state" value={<StateChip value={data.billingAccount?.reconciliationStatus} />} />
            <KeyValue label="Customer" value={data.billingAccount?.paddleCustomerId} mono />
            <KeyValue label="Business" value={data.billingAccount?.paddleBusinessId} mono />
            <KeyValue label="Address" value={data.billingAccount?.paddleAddressId} mono />
          </dl>
        </Panel>
      </div>
      {data.operation && (
        <Panel title="Current onboarding operation" description="Progress is derived from persisted facts; no hand-ticked completion.">
          <div className="grid gap-3 p-4 sm:grid-cols-3">
            <div><div className="text-[11px] text-muted">Workflow state</div><div className="mt-1"><StateChip value={data.operation.state} /></div></div>
            <div><div className="text-[11px] text-muted">Collection</div><div className="mt-1 text-xs font-medium">{humanize(data.operation.collectionVariant)}</div></div>
            <div><div className="text-[11px] text-muted">Payment link</div><div className="mt-1 text-xs font-medium">{data.operation.checkoutEnabled ? "Enabled by exception" : "Disabled"}</div></div>
          </div>
        </Panel>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <ClientInvoices invoices={data.recentInvoices.slice(0, 3)} compact />
        <Panel title="Pinned operating note" description="The most important internal context for this client.">
          {pinnedNote ? (
            <div className="p-4">
              <p className="whitespace-pre-wrap text-sm leading-6">{pinnedNote.body}</p>
              <div className="mt-3 text-[11px] text-muted">Updated {formatDate(pinnedNote.updatedAt, true)}</div>
            </div>
          ) : <div className="p-8 text-center text-sm text-muted">No pinned note.</div>}
        </Panel>
      </div>
    </div>
  );
}

function ClientOnboarding({ data, onOpen }: { data: ClientAggregate; onOpen(): void }) {
  if (!data.operation) {
    return <Callout tone="warning" title="No onboarding operation exists">Start the controlled workflow from the client header. Creating a record alone never invoices or grants access.</Callout>;
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Workflow" value={<StateChip value={data.operation.state} />} detail={`Version ${data.operation.workflowVersion}`} />
        <Metric label="Purchase" value={<StateChip value={data.onboarding?.status} />} detail={data.onboarding?.invoiceNumber ?? "No invoice number"} />
        <Metric label="Subscription" value={<StateChip value={data.subscription?.status} />} detail={data.operation.subscriptionId ?? "Not created"} />
        <Metric label="Access" value={<StateChip value={data.company.eexEnabled ? "active" : "pending"} />} detail={data.profile?.lifecycle === "live" ? "Live lifecycle" : "Not live yet"} />
      </div>
      <Panel title="Controlled onboarding operation" description="Commercial setup, legal invoice identity, collection, Paddle issuance, payment, and technical setup stay visible as separate domains.">
        <div className="grid gap-4 p-4 md:grid-cols-2">
          <dl>
            <KeyValue label="Collection" value={humanize(data.operation.collectionVariant)} />
            <KeyValue label="Payment link" value={data.operation.checkoutEnabled ? "Enabled by exception" : "Disabled"} />
            <KeyValue label="Transaction" value={data.operation.transactionId} mono />
          </dl>
          <dl>
            <KeyValue label="Invoice due" value={formatDate(data.onboarding?.dueAt)} />
            <KeyValue label="Paid" value={formatDate(data.onboarding?.paidAt, true)} />
            <KeyValue label="Last blocker" value={data.operation.lastErrorCode ? humanize(data.operation.lastErrorCode) : "None"} />
          </dl>
        </div>
        <div className="flex justify-end border-t border-border/70 p-3"><Button variant="primary" onClick={onOpen}>Open full onboarding workspace <ArrowRight size={13} /></Button></div>
      </Panel>
    </div>
  );
}

function ClientProfile({ data, onSaved }: { data: ClientAggregate; onSaved(): void }) {
  const profile = data.profile;
  const [form, setForm] = useState({
    segment: profile?.segment ?? "",
    primaryContactName: profile?.primaryContactName ?? "",
    primaryContactEmail: profile?.primaryContactEmail ?? "",
    primaryContactPhone: profile?.primaryContactPhone ?? "",
    contractReference: profile?.contractReference ?? "",
    orderFormRef: profile?.orderFormRef ?? "",
    committedTermStart: profile?.committedTermStart ?? "",
    committedTermEnd: profile?.committedTermEnd ?? "",
    poRequired: profile?.poRequired ?? false,
    moduleIntent: profile?.moduleIntent ?? {},
    negotiatedTermsNote: profile?.negotiatedTermsNote ?? "",
  });
  const mutation = useMutation({
    mutationFn: () => api(`/admin/clients/${data.company.id}/profile`, { method: "PATCH", body: JSON.stringify({
      ...form,
      segment: form.segment || null,
      primaryContactName: form.primaryContactName || null,
      primaryContactEmail: form.primaryContactEmail || null,
      primaryContactPhone: form.primaryContactPhone || null,
      contractReference: form.contractReference || null,
      orderFormRef: form.orderFormRef || null,
      committedTermStart: form.committedTermStart || null,
      committedTermEnd: form.committedTermEnd || null,
      negotiatedTermsNote: form.negotiatedTermsNote || null,
    }) }),
    onSuccess: onSaved,
  });
  return (
    <div className="space-y-4">
      <Panel title="Cascena profile" description="Operational contacts and negotiated terms may differ from the Paddle legal invoice recipient.">
        <form className="space-y-4 p-4" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Segment"><Input value={form.segment} onChange={(event) => setForm({ ...form, segment: event.target.value })} /></FormField>
          <FormField label="Lifecycle"><div className="flex h-9 items-center rounded-md border border-border px-3"><StateChip value={profile?.lifecycle} /></div></FormField>
          <FormField label="Primary contact"><Input value={form.primaryContactName} onChange={(event) => setForm({ ...form, primaryContactName: event.target.value })} /></FormField>
          <FormField label="Contact email"><Input type="email" value={form.primaryContactEmail} onChange={(event) => setForm({ ...form, primaryContactEmail: event.target.value })} /></FormField>
          <FormField label="Contact phone"><Input value={form.primaryContactPhone} onChange={(event) => setForm({ ...form, primaryContactPhone: event.target.value })} /></FormField>
        </div>
        <div className="border-t border-border/70 pt-4">
          <div className="mb-3 text-xs font-semibold text-fg">Commercial file</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Contract reference"><Input value={form.contractReference} onChange={(event) => setForm({ ...form, contractReference: event.target.value })} /></FormField>
            <FormField label="Order form reference"><Input value={form.orderFormRef} onChange={(event) => setForm({ ...form, orderFormRef: event.target.value })} /></FormField>
            <FormField label="Committed term starts"><Input type="date" value={form.committedTermStart} onChange={(event) => setForm({ ...form, committedTermStart: event.target.value })} /></FormField>
            <FormField label="Committed term ends"><Input type="date" min={form.committedTermStart || undefined} value={form.committedTermEnd} onChange={(event) => setForm({ ...form, committedTermEnd: event.target.value })} /></FormField>
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={form.poRequired} onChange={(event) => setForm({ ...form, poRequired: event.target.checked })} /> Purchase order is required</label>
        </div>
        <div>
          <div className="text-xs font-medium text-fg">Contracted module intent</div>
          <p className="mt-0.5 text-[11px] text-muted">Intent informs onboarding; it does not grant access by itself.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {MODULES.map(([key, label]) => <label key={key} className="inline-flex items-center gap-2 rounded-md border border-border px-2.5 py-2 text-xs"><input type="checkbox" checked={Boolean(form.moduleIntent[key])} onChange={(event) => setForm({ ...form, moduleIntent: { ...form.moduleIntent, [key]: event.target.checked } })} />{label}</label>)}
          </div>
        </div>
        <Callout title="Lifecycle is derived">A client becomes live only after the onboarding workspace verifies contract, payment, subscription, administrator, configuration, and module access.</Callout>
        <FormField label="Negotiated terms note"><Textarea value={form.negotiatedTermsNote} onChange={(event) => setForm({ ...form, negotiatedTermsNote: event.target.value })} /></FormField>
        {mutation.isError && <Callout tone="danger" title="Profile was not saved">{errorMessage(mutation.error)}</Callout>}
        <div className="flex justify-end"><Button type="submit" variant="primary" disabled={mutation.isPending}>{mutation.isPending ? "Saving…" : "Save profile"}</Button></div>
        </form>
      </Panel>
      <PaddleBillingParty clientId={data.company.id} billingAccount={data.billingAccount} />
    </div>
  );
}

function PaddleBillingParty({ clientId, billingAccount }: {
  clientId: string;
  billingAccount: ClientAggregate["billingAccount"];
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PaddlePartyDraft | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const partyQ = useQuery({
    queryKey: ["admin", "client", clientId, "billing-party"],
    queryFn: () => api<PaddlePartyResponse>(`/admin/clients/${clientId}/billing-party`),
    enabled: Boolean(billingAccount?.paddleCustomerId && billingAccount.paddleAddressId),
    retry: false,
    staleTime: 90_000,
  });
  const party = partyQ.data?.party;
  const business = party?.businesses[0] ?? null;
  const address = party?.addresses[0] ?? null;
  const reconciliationStatus = partyQ.data?.reconciliationStatus
    ?? billingAccount?.reconciliationStatus
    ?? "unconfigured";
  const updateM = useMutation({
    mutationFn: (next: PaddlePartyDraft) => api<PaddlePartyResponse>(
      `/admin/clients/${clientId}/billing-party`,
      { method: "PATCH", body: JSON.stringify(paddlePartyPayload(next)) }
    ),
    onSuccess: async (result) => {
      queryClient.setQueryData(["admin", "client", clientId, "billing-party"], result);
      setDraft(null);
      setConfirmed(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "inbox"] }),
      ]);
    },
  });
  const reconcileM = useMutation({
    mutationFn: () => api<PaddlePartyResponse>(
      `/admin/clients/${clientId}/billing-party/reconcile`,
      { method: "POST" }
    ),
    onSuccess: async (result) => {
      queryClient.setQueryData(["admin", "client", clientId, "billing-party"], result);
      setDraft(null);
      setConfirmed(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "inbox"] }),
      ]);
    },
  });
  const actions = partyQ.data ? (
    <div className="flex flex-wrap gap-2">
      <Button variant="quiet" disabled={partyQ.isFetching} onClick={() => partyQ.refetch()}>
        <RefreshCw size={13} /> {partyQ.isFetching ? "Refreshing…" : "Refresh live"}
      </Button>
      {reconciliationStatus === "ready" && !draft && party && address && (
        <Button onClick={() => { setDraft(paddlePartyDraft(party)); setConfirmed(false); }}>
          <Pencil size={13} /> Edit future invoice party
        </Button>
      )}
    </div>
  ) : undefined;
  return (
    <Panel
      title="Invoice party — live from Paddle"
      description="Read through on demand; names, email, tax identifiers, contacts, and address are not copied into Cascena or audit metadata. Issued invoices remain immutable."
      action={actions}
    >
      {!billingAccount?.paddleCustomerId ? (
        <div className="p-8 text-center text-sm text-muted">No Paddle invoice party is bound yet. Create or select it in Onboarding.</div>
      ) : partyQ.isLoading ? (
        <div className="p-8 text-center text-sm text-muted">Reading the live Paddle party…</div>
      ) : partyQ.isError || !party || !address ? (
        <div className="p-4"><Callout tone="danger" title="Live invoice party could not be read">Use the Onboarding reconciliation flow before issuing another invoice.</Callout></div>
      ) : draft ? (
        <form className="space-y-5 p-4" onSubmit={(event) => { event.preventDefault(); updateM.mutate(draft); }}>
          <Callout title={`${draft.partyKind === "business" ? "Business" : "Individual"} identity is locked`}>
            You can correct fields on the currently bound Paddle entities, but you cannot switch party type or replace their IDs here. That requires a separate identity-replacement workflow.
          </Callout>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div><h4 className="text-xs font-semibold text-fg">Paddle customer</h4><p className="mt-0.5 text-[11px] text-muted">Used for delivery and customer-level billing identity.</p></div>
              <FormField label="Customer name"><Input value={draft.customerName} onChange={(event) => setDraft({ ...draft, customerName: event.target.value })} /></FormField>
              <FormField label="Billing email" required><Input required type="email" value={draft.customerEmail} onChange={(event) => setDraft({ ...draft, customerEmail: event.target.value })} /></FormField>
            </div>
            {draft.partyKind === "business" && (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <div><h4 className="text-xs font-semibold text-fg">Legal business</h4><p className="mt-0.5 text-[11px] text-muted">These fields appear on future Paddle billing records.</p></div>
                <FormField label="Legal name" required><Input required value={draft.businessName} onChange={(event) => setDraft({ ...draft, businessName: event.target.value })} /></FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Company number"><Input value={draft.companyNumber} onChange={(event) => setDraft({ ...draft, companyNumber: event.target.value })} /></FormField>
                  <FormField label="Tax identifier"><Input value={draft.taxIdentifier} onChange={(event) => setDraft({ ...draft, taxIdentifier: event.target.value })} /></FormField>
                </div>
              </div>
            )}
          </div>
          {draft.partyKind === "business" && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><h4 className="text-xs font-semibold text-fg">Invoice contacts</h4><p className="mt-0.5 text-[11px] text-muted">Optional recipients stored only in Paddle.</p></div>
                <Button type="button" disabled={draft.contacts.length >= 10} onClick={() => setDraft({ ...draft, contacts: [...draft.contacts, { name: "", email: "" }] })}><Plus size={13} /> Add contact</Button>
              </div>
              {draft.contacts.length === 0 ? <p className="text-xs text-muted">No additional invoice contacts.</p> : draft.contacts.map((contact, index) => (
                <div key={index} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto]">
                  <Input aria-label={`Invoice contact ${index + 1} name`} placeholder="Name" value={contact.name} onChange={(event) => setDraft({ ...draft, contacts: draft.contacts.map((item, contactIndex) => contactIndex === index ? { ...item, name: event.target.value } : item) })} />
                  <Input aria-label={`Invoice contact ${index + 1} email`} required type="email" placeholder="Email" value={contact.email} onChange={(event) => setDraft({ ...draft, contacts: draft.contacts.map((item, contactIndex) => contactIndex === index ? { ...item, email: event.target.value } : item) })} />
                  <Button aria-label={`Remove invoice contact ${index + 1}`} type="button" variant="quiet" onClick={() => setDraft({ ...draft, contacts: draft.contacts.filter((_, contactIndex) => contactIndex !== index) })}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3 rounded-lg border border-border p-3">
            <div><h4 className="text-xs font-semibold text-fg">Billing address</h4><p className="mt-0.5 text-[11px] text-muted">Country uses a two-letter ISO code.</p></div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <FormField label="Description"><Input value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></FormField>
              <FormField label="Address line 1"><Input value={draft.firstLine} onChange={(event) => setDraft({ ...draft, firstLine: event.target.value })} /></FormField>
              <FormField label="Address line 2"><Input value={draft.secondLine} onChange={(event) => setDraft({ ...draft, secondLine: event.target.value })} /></FormField>
              <FormField label="City"><Input value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} /></FormField>
              <FormField label="Postal code"><Input value={draft.postalCode} onChange={(event) => setDraft({ ...draft, postalCode: event.target.value })} /></FormField>
              <FormField label="Region"><Input value={draft.region} onChange={(event) => setDraft({ ...draft, region: event.target.value })} /></FormField>
              <FormField label="Country code" required><Input required minLength={2} maxLength={2} value={draft.countryCode} onChange={(event) => setDraft({ ...draft, countryCode: event.target.value.toUpperCase() })} /></FormField>
            </div>
          </div>
          <Callout tone="warning" title="Future billing records only">Changing this live Paddle party does not rewrite invoices already issued. Verify the legal recipient before saving.</Callout>
          <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-xs"><input className="mt-0.5" type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span><span className="block font-medium text-fg">I verified this is the correct legal invoice recipient.</span><span className="text-muted">The update is written directly to the bound live Paddle customer, business, and address.</span></span></label>
          {updateM.isError && <Callout tone="danger" title="Paddle party was not safely updated">{errorMessage(updateM.error)} Reconcile before trying again.</Callout>}
          <div className="flex flex-wrap justify-end gap-2"><Button type="button" disabled={updateM.isPending} onClick={() => { setDraft(null); setConfirmed(false); }}>Cancel</Button><Button type="submit" variant="primary" disabled={updateM.isPending || !confirmed}>{updateM.isPending ? "Updating Paddle…" : "Update live Paddle party"}</Button></div>
        </form>
      ) : (
        <div className="space-y-4 p-4">
          {reconciliationStatus !== "ready" && <Callout tone="danger" title="Paddle party needs reconciliation">State: {humanize(reconciliationStatus)}. Editing and invoice preparation remain blocked until the exact bound entities can be read back from Paddle.<div className="mt-2"><Button variant="danger" disabled={reconcileM.isPending} onClick={() => reconcileM.mutate()}><RefreshCw size={13} /> {reconcileM.isPending ? "Reconciling…" : "Reconcile exact IDs"}</Button></div></Callout>}
          {reconcileM.isError && <Callout tone="danger" title="Reconciliation did not complete">{errorMessage(reconcileM.error)}</Callout>}
          <div className="grid gap-5 md:grid-cols-3">
          <dl>
            <KeyValue label="Customer" value={party.customer.name} />
            <KeyValue label="Email" value={party.customer.email} />
            <KeyValue label="Customer ID" value={party.customer.id} mono />
          </dl>
          <dl>
            <KeyValue label="Legal party" value={business?.name ?? "Individual"} />
            <KeyValue label="Company no." value={business?.companyNumber} />
            <KeyValue label="Tax identifier" value={business?.taxIdentifier} />
            <KeyValue label="Invoice contacts" value={business?.contacts.length ? business.contacts.map((contact) => contact.email).join(", ") : "—"} />
          </dl>
          <dl>
            <KeyValue label="Address" value={[address.firstLine, address.secondLine].filter(Boolean).join(", ") || "—"} />
            <KeyValue label="City / region" value={[address.postalCode, address.city, address.region].filter(Boolean).join(" ") || "—"} />
            <KeyValue label="Country" value={address.countryCode} />
            <KeyValue label="Live read" value={formatDate(partyQ.data?.readAt, true)} />
          </dl>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-[11px] text-muted"><span>Party state <StateChip value={reconciliationStatus} /></span><span>Last reconciled {formatDate(partyQ.data?.lastReconciledAt, true)}</span></div>
        </div>
      )}
    </Panel>
  );
}

function paddlePartyDraft(party: PaddlePartyResponse["party"]): PaddlePartyDraft {
  const business = party.businesses[0] ?? null;
  const address = party.addresses[0]!;
  return {
    partyKind: business ? "business" : "individual",
    customerName: party.customer.name ?? "",
    customerEmail: party.customer.email,
    businessName: business?.name ?? "",
    companyNumber: business?.companyNumber ?? "",
    taxIdentifier: business?.taxIdentifier ?? "",
    contacts: business?.contacts.map((contact) => ({
      name: contact.name ?? "",
      email: contact.email,
    })) ?? [],
    description: address.description ?? "",
    firstLine: address.firstLine ?? "",
    secondLine: address.secondLine ?? "",
    city: address.city ?? "",
    postalCode: address.postalCode ?? "",
    region: address.region ?? "",
    countryCode: address.countryCode,
  };
}

function paddlePartyPayload(draft: PaddlePartyDraft) {
  const common = {
    customer: {
      name: blankToNull(draft.customerName),
      email: draft.customerEmail.trim(),
    },
    address: {
      description: blankToNull(draft.description),
      firstLine: blankToNull(draft.firstLine),
      secondLine: blankToNull(draft.secondLine),
      city: blankToNull(draft.city),
      postalCode: blankToNull(draft.postalCode),
      region: blankToNull(draft.region),
      countryCode: draft.countryCode.trim().toUpperCase(),
    },
  };
  return draft.partyKind === "business" ? {
    partyKind: "business" as const,
    ...common,
    business: {
      name: draft.businessName.trim(),
      companyNumber: blankToNull(draft.companyNumber),
      taxIdentifier: blankToNull(draft.taxIdentifier),
      contacts: draft.contacts.map((contact) => ({
        name: blankToNull(contact.name),
        email: contact.email.trim(),
      })),
    },
  } : { partyKind: "individual" as const, ...common };
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function ClientInvoices({ invoices, compact = false }: {
  invoices: ClientAggregate["recentInvoices"];
  compact?: boolean;
}) {
  return (
    <Panel title={compact ? "Recent invoices" : "Invoices and transactions"} description="Only Cascena-attributed Paddle records are shown.">
      {invoices.length === 0 ? <div className="p-8 text-center text-sm text-muted">No attributed transactions yet.</div> : (
        <div className="ops-admin-table-scroll overflow-x-auto"><table className="w-full min-w-[720px] text-xs"><thead className="border-b border-border bg-border/10 text-left text-muted"><tr><th className="p-3">Invoice</th><th className="p-3">Status</th><th className="p-3">Billed</th><th className="p-3">Due</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">PDF</th></tr></thead><tbody>{invoices.map((invoice) => { const pdfAvailable = clientInvoicePdfAvailable(invoice); return <tr key={invoice.paddleTransactionId} className="border-b border-border/50 last:border-0"><td className="p-3"><div className="font-medium">{invoice.invoiceNumber ?? "Prepared transaction"}</div><div className="mt-0.5 font-mono text-[10px] text-muted">{invoice.paddleTransactionId}</div></td><td className="p-3"><StateChip value={invoice.status} /></td><td className="p-3">{formatDate(invoice.billedAt)}</td><td className="p-3">{formatDate(invoice.dueAt)}</td><td className="p-3 text-right font-medium tabular-nums">{formatMoney(invoice.totalMinor, invoice.currency)}</td><td className="p-3 text-right">{pdfAvailable ? <a className="text-accent hover:underline" href={`/api/admin/transactions/${invoice.paddleTransactionId}/invoice-pdf`} target="_blank" rel="noreferrer">Open</a> : <span className="text-muted">Unavailable</span>}</td></tr>; })}</tbody></table></div>
      )}
    </Panel>
  );
}

function ClientSubscription({ clientId, subscription, subscriptionView, paymentTermsDays }: {
  clientId: string;
  subscription: ClientAggregate["subscription"];
  subscriptionView: ClientAggregate["subscriptionView"];
  paymentTermsDays: number | null;
}) {
  const queryClient = useQueryClient();
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const portalM = useMutation({
    mutationFn: () => api<{ overviewUrl: string }>(`/admin/clients/${clientId}/portal-session`, { method: "POST" }),
    onSuccess: (result) => setPortalUrl(result.overviewUrl),
  });
  const resyncM = useMutation({
    mutationFn: () => api(`/admin/clients/${clientId}/resync-paddle`, { method: "POST" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "billing", clientId] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "inbox"] });
    },
  });
  if (!subscription) {
    return <Callout tone="warning" title="No subscription history">This client has no subscription record. If EEX access is enabled, the Inbox flags it as legacy access.</Callout>;
  }
  return (
    <div className="space-y-4">
      {subscriptionView === "latest_canceled" && <Callout tone="warning" title="Canceled-only account">The latest historical subscription is shown below. It does not renew and is not a current subscription.</Callout>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button disabled={subscriptionView !== "current" || !subscription.paddleSubscriptionId || resyncM.isPending} onClick={() => resyncM.mutate()}>{resyncM.isPending ? "Resyncing…" : "Resync from Paddle"}</Button>
        <Button disabled={portalM.isPending} onClick={() => portalM.mutate()}>{portalM.isPending ? "Creating portal…" : "Create customer portal link"}</Button>
        {portalUrl && <a href={portalUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-8 items-center rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white">Open generated portal</a>}
      </div>
      {(portalM.isError || resyncM.isError) && <Callout tone="danger" title="Paddle action failed">{errorMessage(portalM.error ?? resyncM.error)}</Callout>}
      <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Subscription facts" description="Mirrored from Paddle events and reconciled live by the billing worker.">
        <dl className="px-4 py-1">
          <KeyValue label="Status" value={<StateChip value={subscription.status} />} />
          <KeyValue label="Tier" value={humanize(subscription.tier)} />
          <KeyValue label="Currency" value={subscription.currency} />
          <KeyValue label="Paddle subscription" value={subscription.paddleSubscriptionId} mono />
        </dl>
      </Panel>
      <Panel title="Collection and lifecycle" description="Pause and cancel are distinct; neither is presented as a completed cancellation.">
        <dl className="px-4 py-1">
          <KeyValue label="Collection" value={subscription.collectionMode ? humanize(subscription.collectionMode) : "—"} />
          <KeyValue label="Latest invoice terms" value={paymentTermsDays != null ? `Net ${paymentTermsDays}` : "—"} />
          <KeyValue label="Current period" value={subscription.currentPeriodStart && subscription.currentPeriodEnd ? `${formatDate(subscription.currentPeriodStart)} – ${formatDate(subscription.currentPeriodEnd)}` : "—"} />
          <KeyValue label="Next billed" value={formatDate(subscription.nextBilledAt, true)} />
          <KeyValue label="Scheduled pause" value={formatDate(subscription.scheduledPauseAt, true)} />
          <KeyValue label="Scheduled cancel" value={formatDate(subscription.scheduledCancelAt, true)} />
          <KeyValue label="Grace until" value={formatDate(subscription.graceUntil, true)} />
        </dl>
      </Panel>
      </div>
    </div>
  );
}

function ClientUsers({ clientId }: { clientId: string }) {
  const usersQ = useQuery({
    queryKey: ["admin", "company-users", clientId],
    queryFn: () => api<{ users: AdminUser[] }>("/admin/company-users", {
      headers: { "x-company-id": clientId },
    }),
  });
  return (
    <div className="space-y-4">
      <Panel title="Create company user" description="Creates a user for this route-bound client; it does not rely on the global company selector.">
        <div className="p-4"><CreateCompanyUserForm companyId={clientId} /></div>
      </Panel>
      <Panel title="Users and module access" description="Seat enforcement remains server-side when EEX access is granted.">
        <div className="p-4">
          {usersQ.isError && <Callout tone="danger" title="Users could not be loaded">{errorMessage(usersQ.error)}</Callout>}
          <UserTable users={usersQ.data?.users ?? []} kind="company" disabled={usersQ.isLoading} companyId={clientId} />
        </div>
      </Panel>
    </div>
  );
}

function ClientTimeline({ clientId, companyName }: { clientId: string; companyName: string }) {
  const [source, setSource] = useState<"all" | "cascena" | "paddle">("all");
  const timelineQ = useQuery({
    queryKey: ["admin", "client", clientId, "timeline"],
    queryFn: () => api<ClientTimelineResponse>(`/admin/clients/${clientId}/timeline?limit=150`),
  });
  const events = (timelineQ.data?.timeline ?? []).filter((event) =>
    source === "all" || event.source === source
  );
  return (
    <Panel
      title="Unified client timeline"
      description={`Cascena operator actions and privacy-projected Paddle webhooks for ${companyName}, ordered by their actual occurrence time.`}
      action={<Select aria-label="Timeline source" value={source} onChange={(event) => setSource(event.target.value as typeof source)}><option value="all">All sources</option><option value="cascena">Cascena</option><option value="paddle">Paddle</option></Select>}
    >
      <div className="space-y-3 p-4">
        <Callout title="One chronology, two sources">Paddle rows expose only operational IDs and status. Buyer, address, tax, and payment-method payloads are never stored in Cascena.</Callout>
        {timelineQ.isLoading ? <div className="p-8 text-center text-sm text-muted">Loading the client timeline…</div> : timelineQ.isError ? <Callout tone="danger" title="Timeline could not be loaded">{errorMessage(timelineQ.error)}</Callout> : events.length === 0 ? <div className="p-8 text-center text-sm text-muted">No {source === "all" ? "" : `${source} `}events yet.</div> : (
          <ol className="relative ml-3 border-l border-border/80">
            {events.map((event) => {
              const Icon = event.source === "paddle" ? Webhook : Activity;
              return <li key={event.id} className="relative pb-5 pl-6 last:pb-1"><span className="absolute -left-3 top-0 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-bg"><Icon size={12} className={event.source === "paddle" ? "text-accent" : "text-muted"} /></span><article className="rounded-lg border border-border/70 p-3"><div className="flex flex-wrap items-start justify-between gap-2"><div><div className="text-sm font-medium text-fg">{humanize(event.title.replaceAll(".", "_"))}</div><div className="mt-0.5 text-[11px] text-muted">{event.actor} · {formatDate(event.at, true)}</div></div><span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${timelineTone(event.severity)}`}>{humanize(event.processingState ?? event.severity)}</span></div>{event.detail && <div className="mt-2 break-words font-mono text-[11px] text-muted">{event.detail}</div>}<div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted"><span>{event.source === "paddle" ? "Paddle webhook" : humanize(event.category)}</span>{event.method && <span>{event.method} {event.route ?? ""}</span>}{event.statusCode != null && <span>HTTP {event.statusCode}</span>}{event.providerEventId && <span className="font-mono">{event.providerEventId}</span>}</div></article></li>;
            })}
          </ol>
        )}
      </div>
    </Panel>
  );
}

function timelineTone(severity: string): string {
  if (["critical", "parked"].includes(severity)) return "border-negRed/30 bg-negRed/10 text-negRed";
  if (["warning", "pending"].includes(severity)) return "border-warnOrange/30 bg-warnOrange/10 text-warnOrange";
  if (["notice", "processed"].includes(severity)) return "border-posGreen/30 bg-posGreen/10 text-posGreen";
  return "border-border bg-border/10 text-muted";
}

function clientInvoicePdfAvailable(invoice: ClientAggregate["recentInvoices"][number]): boolean {
  if (!invoice.invoiceNumber || !invoice.totalMinor || invoice.totalMinor <= 0) return false;
  if (invoice.collectionMode === "manual") return ["billed", "completed"].includes(invoice.status);
  return invoice.collectionMode === "automatic" && invoice.status === "completed";
}

function ClientNotes({ clientId, notes }: { clientId: string; notes: ClientAggregate["notes"] }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const mutation = useMutation({
    mutationFn: () => api(`/admin/clients/${clientId}/notes`, { method: "POST", body: JSON.stringify({ body, pinned }) }),
    onSuccess: async () => { setBody(""); setPinned(false); await queryClient.invalidateQueries({ queryKey: ["admin", "client", clientId] }); },
  });
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <Panel title="Owner notes" description="Internal operational context; note contents are excluded from audit metadata.">
        {notes.length === 0 ? <div className="p-8 text-center text-sm text-muted">No notes yet.</div> : <div className="divide-y divide-border/60">{notes.map((note) => <article key={note.id} className="p-4"><div className="flex items-center justify-between gap-3"><span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted"><StickyNote size={12} /> {note.pinned ? "Pinned" : "Note"}</span><time className="text-[11px] text-muted">{formatDate(note.createdAt, true)}</time></div><p className="mt-2 whitespace-pre-wrap text-sm leading-5">{note.body}</p></article>)}</div>}
      </Panel>
      <Panel title="Add note"><form className="space-y-3 p-4" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}><Textarea required value={body} onChange={(event) => setBody(event.target.value)} placeholder="Add context for the operations team" /><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={pinned} onChange={(event) => setPinned(event.target.checked)} /> Pin to the top</label>{mutation.isError && <div className="text-xs text-negRed">{errorMessage(mutation.error)}</div>}<Button type="submit" variant="primary" disabled={mutation.isPending || !body.trim()}>{mutation.isPending ? "Adding…" : "Add note"}</Button></form></Panel>
    </div>
  );
}
